// src/components/shell/popovers/ActivityBannerAchievementsSection.tsx
// Compact achievements panel rendered inside ActivityBannerPopover. Replaces
// the placeholder that used to live in ActivityBannerPopover.tsx. Doctrine
// section 4: achievements should be promoted to a persistent top-level
// position. Full gallery (with revoke controls for Ads guidance editors)
// stays in AchievementGalleryDrawer; this section is a glance-level view +
// "View all" entry point.
//
// `open` is forwarded from the parent popover so the fetch is gated — when
// the activity banner is closed, no network call happens.
import { useState } from "react";
import { AchievementBadge } from "@/components/common/kpi/AchievementBadge";
import { AchievementGalleryDrawer } from "@/components/common/kpi/AchievementGalleryDrawer";
import { useUserAchievements } from "@/hooks/useUserAchievements";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";

export type ActivityBannerAchievementsSectionProps = {
  /** Whether the parent popover is open — gates the fetch. */
  open: boolean;
};

const VISIBLE_CHIPS = 5;

export function ActivityBannerAchievementsSection({ open }: ActivityBannerAchievementsSectionProps) {
  const scopedOrgId = useScopedOrganizationId();
  const [galleryOpen, setGalleryOpen] = useState(false);

  const vm = useUserAchievements({
    enabled: open,
    scopedOrgId,
  });

  return (
    <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Achievements
        </div>
        {vm.achievements.length > 0 ? (
          <button
            type="button"
            onClick={() => setGalleryOpen(true)}
            className="text-[11px] font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-300 dark:hover:text-indigo-200"
          >
            View all -&gt;
          </button>
        ) : null}
      </div>
      <AchievementsBody vm={vm} onViewAll={() => setGalleryOpen(true)} />
      <AchievementGalleryDrawer open={galleryOpen} onClose={() => setGalleryOpen(false)} />
    </div>
  );
}

type AchievementsBodyProps = {
  vm: ReturnType<typeof useUserAchievements>;
  onViewAll: () => void;
};

function AchievementsBody({ vm, onViewAll }: AchievementsBodyProps) {
  if (vm.loading && vm.achievements.length === 0) {
    return (
      <div className="mt-2 flex flex-wrap gap-2" aria-label="Loading achievements">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="inline-block h-5 w-20 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700"
            aria-hidden="true"
          />
        ))}
      </div>
    );
  }

  if (vm.error) {
    return (
      <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
        Couldn&rsquo;t load achievements. {vm.error}
      </p>
    );
  }

  if (vm.achievements.length === 0) {
    return (
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        No achievements yet &mdash; keep going!
      </p>
    );
  }

  const visible = vm.achievements.slice(0, VISIBLE_CHIPS);
  const overflowCount = vm.achievements.length - visible.length;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {visible.map((a) => (
        <AchievementBadge
          key={a.id}
          achievementKey={a.achievement_key}
          metadata={a.metadata}
        />
      ))}
      {overflowCount > 0 ? (
        <button
          type="button"
          onClick={onViewAll}
          className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:ring-gray-700 dark:hover:bg-gray-700"
          aria-label={`Show ${overflowCount} more achievements`}
        >
          +{overflowCount} more
        </button>
      ) : null}
    </div>
  );
}
